<script>
	import { browser } from '$app/environment';
	import { goto } from '$app/navigation';
	import { resolve } from '$app/paths';
	import { page } from '$app/state';
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import {
		handleFormSubmit,
		sendPixelConversions,
	} from '$lib/PageSections/Forms/formUtils';
	import toast from 'svelte-french-toast';
	import { fly } from 'svelte/transition';
	import SuperDebug, { defaults, superForm } from 'sveltekit-superforms';
	import { valibotClient } from 'sveltekit-superforms/adapters';

	let {
		// eslint-disable-next-line no-useless-assignment
		form = $bindable(),
		formData = null,
		resetForm = true,
		enctype = null,
		taintedMessage = null,
		class: className = null,
		data = null,
		initialFormData = null,
		formName,
		disabled = null,
		schema,
		// eslint-disable-next-line no-useless-assignment
		innerForm = $bindable(),
		buttonText = 'Submit',
		showButton = true,
		taintedButton = false,
		invalidateAll = 'force',
		customValidity = null,
		sendPixelEvents = null,
		action,
		children,
		debug = null,
		buttonCenter = null,
		defaultValues = null,
		method = 'POST',
		onSuccess = null,
		onBeforeSubmit = null,
		onUpdated = null,
	} = $props();

	function getForm({ schema, initialFormData, invalidateAll }) {
		return superForm(
			defaultValues
				? defaults(defaultValues, initialFormData)
				: initialFormData,
			{
				multipleSubmits: 'abort',
				invalidateAll: invalidateAll,
				customValidity,
				taintedMessage,
				resetForm,
				validators: valibotClient(schema),
				async onSubmit(form) {
					if (onBeforeSubmit) {
						await onBeforeSubmit(form);
					}
					form.formData.set('url', page.url?.toString());
					handleFormSubmit(form.formData, page.url);
				},
				onUpdated({ form }) {
					if (onUpdated) {
						onUpdated(form);
					}
					if (!form.valid) {
						if (form.message?.text) {
							toast.error(form.message.text);
						}
					}
					if (form.valid) {
						if (onSuccess) {
							onSuccess(form);
						}
						if (form.message?.text) {
							toast.success(form.message.text);
						}
						if (sendPixelEvents && form.message.normalizedData) {
							sendPixelConversions(form.message.normalizedData);
						}
						if (form.message.successUrl) {
							if (form.message.hardRedirect) {
								window.location = form.message.successUrl;
							} else {
								goto(resolve(form.message.successUrl));
							}
						}
					}
				},
				onError({ result }) {
					// Ignore aborted requests triggered by starting a new submission
					if (
						result?.type === 'error' &&
						(result?.error?.name === 'AbortError' ||
							result?.error?.code === 'ABORT_ERR')
					) {
						return;
					}
					if (result?.error?.message) {
						toast.error(result?.error?.message);
						$message = result?.error?.message;
					} else {
						$message = 'An error occurred. Sorry about that.';
						toast.error('An error occurred. Sorry about that.');
					}
				},
			},
		);
	}

	// svelte-ignore state_referenced_locally
	initialFormData =
		initialFormData ??
		data?.forms?.[formName] ??
		formData ??
		valibotClient(schema);

	// svelte-ignore state_referenced_locally
	form = getForm({ schema, initialFormData, invalidateAll });
	const {
		message,
		enhance,
		tainted,
		isTainted,
		errors,
		formId,
		delayed,
		submit,
	} = form;
	innerForm = form.form;

	export function submitForm() {
		submit();
	}
</script>

{#if debug}
	<SuperDebug data={innerForm} />
{/if}
<form
	{method}
	{enctype}
	use:enhance
	{action}
	class={[
		`
    row-auto mb-4 grid w-full grid-cols-1 gap-x-4
    last:mb-0
    md:grid-cols-2
  `,
		className,
	]}
>
	<input type="hidden" name="__superform_id" bind:value={$formId} />
	{@render children({
		form,
		disabled,
		loading: $delayed,
		errors: $errors,
		message: $message?.text ?? $message,
		tainted: !browser || isTainted($tainted),
	})}

	<span class="@container col-span-full grid w-full">
		{#if $message || $errors?._errors}
			<div
				transition:fly={{ y: '-50%' }}
				class={[
					`
       row-start-2 flex min-h-12 w-full
       @md:w-auto
     `,
					{ '@sm:row-start-1': !buttonCenter },
				]}
			>
				{#if $message}
					<p
						transition:fly={{ y: '-50%' }}
						class={[
							`
         mb-0 w-full self-center pr-2 text-left
         @md:w-auto
       `,
							{
								'text-indow-green!': page.status == 200,
								'text-indow-red!': page.status >= 400,
							},
						]}
					>
						{$message?.text ?? $message}
					</p>
				{/if}
				{#if $errors?._errors}
					<p
						transition:fly={{ y: '-50%' }}
						class="
        mb-0 w-full self-center pr-2 text-left text-indow-red
        @md:w-auto
      "
					>
						{$errors?._errors}
					</p>
				{/if}
			</div>
		{/if}
		{#if showButton}
			<div
				class={[
					`
       row-start-1 min-h-12 w-full
       @md:w-auto @md:max-w-fit
     `,
					{ 'place-self-end': !buttonCenter },
					{ 'place-self-center': buttonCenter },
				]}
			>
				<Button
					class="mb-0 w-full place-self-end"
					transitionDuration="300"
					buttonType="button"
					loading={$delayed}
					active={taintedButton ? isTainted($tainted) || !browser : true}
					{disabled}>{buttonText}</Button
				>
			</div>
		{/if}
	</span>
</form>
